/* eslint-disable import/prefer-default-export */

import { captureMessage } from "@sentry/nextjs";
import { flatten, uniq } from "lodash";
import type { Accept } from "react-dropzone";
import { assertPresence } from "utils/assertions";

export type MimeType = string;

export const GZIP_MIME_TYPES = [
  "application/gzip",
  "application/x-gzip",
  "application/x-gzip-compressed",
] as const;

export const GZIP_FILE_EXTENSION = "gz";

export const EXTENSIONS_BY_MIME_TYPE: {
  [mimeType: MimeType]: string | undefined;
} = {
  "application/gzip": GZIP_FILE_EXTENSION,
  "application/json": "json",
  "application/pdf": "pdf",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/x-gzip": GZIP_FILE_EXTENSION,
  "application/x-gzip-compressed": GZIP_FILE_EXTENSION,
  "application/xml": "xml",
  "application/zip": "zip",
  "text/csv": "csv",
  "text/plain": "txt",
  "text/txt": "txt",
  "text/xml": "xml",
};

export function getExtensionsForMimeTypes(mimeTypes: MimeType[]) {
  const extensions = uniq(
    mimeTypes.map((mt) => {
      const extension = EXTENSIONS_BY_MIME_TYPE[mt];
      assertPresence(extension, `Could not determine extension for ${mt}!`);
      return extension;
    }),
  );
  extensions.sort();
  return extensions;
}

export function getOrGuessExtension(mimeType: MimeType) {
  const ext = EXTENSIONS_BY_MIME_TYPE[mimeType];
  if (ext) return ext;
  captureMessage(
    `Could not get extension for MIME type: ${mimeType}`,
    "warning",
  );
  const parts = mimeType.split("/");
  if (parts.length < 2) return null;
  return parts.slice(-1)[0];
}

export function getAcceptForMimeTypes(mimeTypes: MimeType[]) {
  return mimeTypes.reduce<Accept>((acc, mimeType) => {
    const extension = EXTENSIONS_BY_MIME_TYPE[mimeType];
    assertPresence(
      extension,
      `Could not get extension for MIME type: ${mimeType}`,
    );
    acc[mimeType] = [`.${extension}`];
    return acc;
  }, {});
}

export function getIsGzipMimeType(
  mimeType: MimeType,
): mimeType is (typeof GZIP_MIME_TYPES)[number] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
  return GZIP_MIME_TYPES.includes(mimeType as any);
}

export function acceptGzipped(accept: Accept) {
  const extensions = uniq(flatten(Object.values(accept)));
  return {
    ...accept,
    ...GZIP_MIME_TYPES.reduce<Accept>((acc, mimeType) => {
      acc[mimeType] = extensions.map((ext) => `.${ext}.${GZIP_FILE_EXTENSION}`);
      return acc;
    }, {}),
  };
}
